(ns ductile.ui.auth
  (:require [applied-science.js-interop :as j]
            [clojure.string :as str]
            [ductile.ui.components :as ui]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.forms :as forms.ui]
            [ductile.ui.http :as http]
            [ductile.ui.icons :as icons]
            [ductile.ui.locale :refer [tr]]
            [ductile.ui.logo :as ui.logo]
            [ductile.ui.router :as router]
            [ductile.ui.routes :as routes]
            [ductile.ui.scene :as scene]
            [ductile.ui.scope]
            [ductile.ui.util :as util]
            [ductile.ui.websocket :as websocket]
            [inside-out.forms :as forms]
            [inside-out.reagent :refer [with-form]]
            [kitchen-async.promise :as p]
            [reagent.core :as r])
  (:require-macros [ductile.ui.scene.macros :refer [defscene]]
                   [ductile.ui.scope.macros :refer [with-scope-protected-value]]))

(def with-hardware-scanner (partial ui/with-hardware-scanner nil))

(defn auth-back-link [{:keys [href label data-testid]}]
  [:a.inline-flex.items-center.gap-2.text-sm.font-medium.text-indigo-700.hover:text-indigo-800.hover:underline
   (cond-> {:href href}
     data-testid (assoc :data-testid data-testid))
   [ui/icon {:size 16} :arrow-left]
   label])

(defn auth-oauth-panel [{:keys [oauth-testid logout-testid explanation explanation-path oauth-href]}]
  [:div.relative.flex.h-full.items-center.justify-center.px-6.py-6.md:px-10.md:py-8
   [:div.hidden.md:absolute.md:right-10.md:top-8.md:block
    [:img.inline-block {:src (ui/maybe-add-host "/images/logo.svg") :width 170}]]
   [:div {:class "w-full max-w-[430px]"}
    [:h2 {:class "mt-0 mb-4 text-center font-bold leading-tight text-[16px] md:text-[18px]"}
     (tr "…or continue with")]
    [:div.space-y-7
     [ui/button {:href (or oauth-href "/oauth/start")
                 :variant :white
                 :size 3
                 :class "relative w-full max-w-[430px]"
                 :data-testid oauth-testid}
      [:span {:class "absolute left-3 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center"}
       icons/microsoft]
      [:span.mx-auto (tr "Your ARS Altmann / Microsoft Login")]]
     [:div.text-center
      [:a (cond-> {:class "text-[14px] text-indigo-700 hover:text-indigo-800 hover:underline"
                   :href "https://login.microsoftonline.com/logout.srf"}
            logout-testid (assoc :data-testid logout-testid))
       (tr "Logout from your Microsoft account…")]]
     [:div.flex.justify-center
      [ui/explain-link {:path (or explanation-path
                                  "/anmeldung/login/")}
       explanation]]]]])

(defn absolute-url? [url]
  (and (string? url)
       (re-find #"^[a-zA-Z][a-zA-Z0-9+.-]*://" url)))

(defn redirect-after-login! [redirect-to]
  (if (absolute-url? redirect-to)
    (router/hard-refresh-to redirect-to)
    (router/navigate-to (or redirect-to "/"))))

(defn form-field-value [^js form field-name]
  (when form
    (some-> form
            (.querySelector (str "[name=\"" field-name "\"]"))
            .-value)))

(defn current-login-params [!state form redirect-to]
  (let [{:keys [email password]} @!state]
    {:email (if form (form-field-value form "email") email)
     :password (if form (form-field-value form "password") password)
     :redirect-to redirect-to}))

(defn submittable-login-params? [{:keys [email password]}]
  (and (not (str/blank? email))
       (not (str/blank? password))))

(defn auth-layout [{:keys [back-link left-content right-panel-opts]}]
  [:div {:class "min-h-screen w-screen text-[14px]"}
   [:div.relative.flex.min-h-screen.flex-col.md:block
    [:section {:class "w-full border-b border-[#dddde2] bg-white md:min-h-screen md:w-1/2 md:border-b-0 md:border-r"}
     [:div {:class "mx-auto flex min-h-full w-full max-w-[430px] flex-col px-6 pt-8 pb-6 md:min-h-screen md:justify-center md:px-10 md:pt-12 md:pb-10"}
      [:div.mb-8.flex.justify-center.md:hidden
       [:img.inline-block {:src (ui/maybe-add-host "/images/logo.svg") :width 100}]]
      (when back-link
        [:div.mb-14
         [auth-back-link back-link]])
      left-content]]
    [:section {:class "w-full max-h-screen overflow-hidden bg-[#f9fbfd] pb-24 md:fixed md:right-0 md:top-0 md:h-screen md:w-1/2 md:max-h-screen md:pb-0"}
     [auth-oauth-panel right-panel-opts]]]])

(defn login-view [{:keys [disabled redirect-to] :as _props}]
  (r/with-let [!state (r/atom (when disabled {:error :user-disabled
                                              :error-message (tr "User has been disabled")}))
               oauth-href (cond-> "/oauth/start"
                            redirect-to (str "?redirect-to=" (js/encodeURIComponent redirect-to)))
               submit-fn (fn [form]
                           (let [login-params (current-login-params !state form redirect-to)]
                             (swap! !state merge login-params {:submitting? true})
                             (p/let [result (http/$post "/auth" login-params)]
                               (if (:error result)
                                 (swap! !state assoc :error result :submitting? false)
                                 (do (deframe/merge-db! result)
                                     (websocket/connect)
                                     (redirect-after-login! (:ductile.auth/redirect-to result)))))))]
    (let [{:keys [email password error submitting?]} @!state]
      (with-hardware-scanner {:view `login-view
                              :on-scan (fn [scan-data]
                                         (when-let [scanned-email (get-in scan-data [:scan/classified :email])]
                                           (swap! !state assoc :email scanned-email)))}
        [:div
         (if submitting?
           [ui/loading-spinner]
           [auth-layout
            {:left-content
             [:div {:class "w-full"}
              [:h1 {:class "mb-4 text-center font-extrabold leading-none text-[24px] md:mb-8 md:text-left md:text-[34px]"}
               (tr "Welcome")]
              [:h2 {:class "mt-0 mb-4 text-center font-bold leading-tight text-[16px] md:text-left md:text-[18px]"}
               (tr "Sign in to your account")]
              (when error
                [:div.mb-3
                 [ui/flash-notice {:type :error
                                   :message (:error-message error)
                                   :on-close #(swap! !state dissoc :error)}]])
              [:form.space-y-4
               {:on-submit (fn [e]
                             (.preventDefault e)
                             (let [login-params (current-login-params !state (.-currentTarget e) redirect-to)]
                               (when (submittable-login-params? login-params)
                                 (submit-fn (.-currentTarget e)))))}
               [ui/with-label
                [:span.text-sm.font-semibold (tr "Email address")]
                [ui/input {:id "email-address"
                           :name "email"
                           :data-testid "auth-login-email"
                           :type "email"
                           :auto-complete "email"
                           :required true
                           :size 3
                           :placeholder (tr "Email address")
                           :value email
                           :on-input #(swap! !state assoc :email (j/get-in % [:target :value]))}]]
               [:div
                [:div.mb-1.flex.items-center.justify-between
                 [:span.text-sm.font-semibold (tr "Password")]
                 [:a {:class "text-[12px] text-indigo-700 hover:text-indigo-800 hover:underline"
                      :href "/auth/forgot-password"
                      :tab-index -1
                      :data-testid "auth-login-forgot-link"}
                  (tr "Forgot your password?")]]
                [ui/input {:id "password"
                           :name "password"
                           :data-testid "auth-login-password"
                           :type "password"
                           :auto-complete "current-password"
                           :required true
                           :size 3
                           :placeholder (tr "Password")
                           :value password
                           :on-input #(swap! !state assoc :password (j/get-in % [:target :value]))}]]
               [ui/button {:variant :primary
                           :size 3
                           :class "mt-1 w-full"
                           :data-testid "auth-login-submit"
                           :type "submit"
                           :disabled? (or (str/blank? email)
                                          (str/blank? password))}
                (tr "Sign in")]
               [:div.flex.justify-center.md:justify-start
                [ui/explain-link {:path "/anmeldung/login/"}
                 (tr "How can I sign in?")]]

               [:div.pt-8
                [:div {:class "mt-0 mb-4 text-center text-[16px] font-semibold leading-tight md:text-left md:text-[18px]"}
                 (tr "No account yet?")]
                [ui/button {:href (router/path '[signup])
                            :variant :white
                            :size 3
                            :class "w-full"
                            :data-testid "auth-login-signup-link"}
                 (tr "Create account")]]]]

             :right-panel-opts {:oauth-testid "auth-login-oauth"
                                :oauth-href oauth-href
                                :logout-testid "auth-login-ms-logout"
                                :explanation-path "/anmeldung/login/#anmeldung-im-browser"
                                :explanation (tr "How can I sign in with my ARS account?")}}])]))))

(defn render-login [_result match]
  [login-view {:disabled (get-in match [:query-params :disabled])
               :redirect-to (get-in match [:query-params :redirect-to])}])


(defn nav-li [path-params {:keys [label template]}]
  [:li
   [:a.text-gray-800.hover:text-green-600.block.px-16.py-4.text-xl.border-b.border-gray-200.flex.items-center.justify-end.text-right.hover:bg-green-100.transition-all.ease-in-out.duration-150
    {:href (router/path template path-params)}
    label
    [ui/icon {:class "ml-2"} :chevron-right]]])

(defn render-frontpage [_result _match]
  [:div.md:bg-gray-200.md:flex.flex-col.items-center.justify-center.h-full
   [:div.pt-0.md:pt-16
    (let [current-user @deframe/!current-user
          compound-name @deframe/!current-compound
          nav-opts {:compound-name compound-name
                    :compound/name compound-name ;; allow for slow migration away from :compound-name in routes
                    :user current-user
                    :country/code (-> current-user :user/company :address/country :country/code)}
          frontpage-items (routes/frontpage-items nav-opts)
          nav-items (routes/build-nav nav-opts)]
      [:div.bg-white.shadow-lg.rounded-lg
       [:div.text-right.md:text-center.border-b.border-gray-200.px-16.py-8
        [:img.inline-block {:src (ui/maybe-add-host (ui.logo/logo compound-name)) :width 200}]]
       (cond
         (seq frontpage-items)
         (into [:ul.text-2xl {:data-testid "frontpage-nav"}]
               (map (fn [item]
                      (with-scope-protected-value item
                        [nav-li nav-opts item])))
               frontpage-items)

         (seq nav-items)
         [:div.mx-auto.text-sm.text-gray-800.p-8.text-center
          [:p.m-3 (tr "Welcome to Ductile!")]
          [:p.m-3 (tr "Please use the navigation menu above to access the application.")]]

         :else
         [:div.mx-auto.text-sm.text-gray-800
          [:p.m-3 (tr "It looks like you don't have permissions to use functionality of the application.")]
          [:p.m-3 (tr "Please get in contact with the compound manager to enable your account.")]])])]])

(defscene login
  [login-view])


(def vat-cc #{"AL" "AT" "BE" "BG" "BY" "CH" "CY" "CZ" "DE" "DK" "EE" "EL" "ES" "FI" "FR" "GB" "HR"
              "HU" "IE" "IL" "IS" "IT" "LT" "LU" "LV" "MK" "MT" "NL" "NO" "PL" "PT" "RO" "RS" "RU" "SE" "SI" "SK" "SM" "TR" "UA" "XI"})

(defn handle-result
  ([result-body]
   (handle-result result-body {}))
  ([result-body {:keys [on-success]}]
   (cond
     (:error-message result-body)
     (forms/message :error (:error-message result-body))

     (:error result-body)
     (forms/message :error (tr "An error has occurred"))

     on-success
     (on-success)

     :else
     (router/hard-refresh-to "/"))))

(defn signup-view []
  (with-form [!form {:user/name ?name
                     :user/email ?email
                     :user/password ?password
                     :user/password-confirmation ?password-confirmation
                     :company/name ?company-name
                     :company/vat-id ?company-vat-id}
              :meta {'?email {:validators [(fn [email _]
                                             (when (and email (not (re-find util/email-regex email)))
                                               {:type :invalid
                                                :content (tr "Email must be valid")}))]}
                     '?company-vat-id {:validators [(fn [vat-id _]
                                                      (when-not (contains? vat-cc (apply str (take 2 vat-id)))
                                                        {:type :invalid
                                                         :content (tr "Please use the international prefix: e.g. IT1234567")}))]}
                     '?password-confirmation {:validators [(fn [confirm {:syms [?password]}]
                                                             (when (not= confirm @?password)
                                                               {:type :invalid
                                                                :content (tr "Password and confirmation must match")}))]}}
              :required [?name ?email ?password ?password-confirmation ?company-name ?company-vat-id]]
    [auth-layout
     {:back-link {:href (router/path '[login])
                  :label (tr "Back to login")
                  :data-testid "auth-signup-back-link"}
      :left-content
      [:form {:class "w-full space-y-5"
              :action "/auth/signup"
              :method :post
              :on-submit (fn [e]
                           (.preventDefault e)
                           (if (forms/submittable? !form)
                             (forms/watch-promise
                              !form
                              (p/let [result (http/$post "/auth/signup" @!form)]
                                (handle-result result)))
                             (forms/touch! !form)))}
       [:h1 {:class "text-center font-bold leading-tight text-[24px] md:text-left"}
        (tr "Create your account")]
       [:div.flex.justify-center.md:justify-start
        [ui/explain-link {:path "/anmeldung/benutzerkonto-erstellen/"}
         (tr "How can I create an account?")]]

       [:h2 {:class "mt-0 mb-4 text-center font-semibold text-slate-500 text-[16px] md:text-left md:text-[18px]"}
        (tr "Account details")]
       [ui/with-label
        [:span.text-sm.font-semibold (tr "Name")]
        [forms.ui/input-text ?name
         {:data-testid "auth-signup-name"
          :size 3
          :placeholder (tr "John Doe")
          :class "w-full"}]]
       [ui/with-label
        [:span.text-sm.font-semibold (tr "Email address")]
        [forms.ui/input-text ?email
         {:data-testid "auth-signup-email"
          :size 3
          :placeholder "email@example.org"
          :class "w-full"}]]
       [ui/with-label
        [:span.text-sm.font-semibold (tr "Password")]
        [forms.ui/input-text ?password
         {:data-testid "auth-signup-password"
          :size 3
          :type "password"
          :class "w-full"}]]
       [ui/with-label
        [:span.text-sm.font-semibold (tr "Password confirmation")]
        [forms.ui/input-text ?password-confirmation
         {:data-testid "auth-signup-password-confirmation"
          :size 3
          :type "password"
          :class "w-full"}]]

       [:h2 {:class "mt-0 mb-4 pt-5 text-center font-semibold text-slate-500 text-[16px] md:text-left md:text-[18px]"}
        (tr "Company details")]
       [ui/with-label
        [:span.text-sm.font-semibold (tr "Company name")]
        [forms.ui/input-text ?company-name
         {:data-testid "auth-signup-company-name"
          :size 3
          :placeholder "ARS Altmann AG"
          :class "w-full"}]]
       [ui/with-label
        [:span.text-sm.font-semibold (tr "VAT ID")]
        [forms.ui/input-text ?company-vat-id
         {:data-testid "auth-signup-vat-id"
          :size 3
          :placeholder (tr "IT123456")
          :class "w-full"}]]

       (into [:div.py-1] (map forms.ui/view-message) (forms/visible-messages !form))

       [ui/button {:variant :primary
                   :size 3
                   :class "w-full"
                   :data-testid "auth-signup-submit"
                   :type "submit"}
        (tr "Create account")]]

      :right-panel-opts {:oauth-testid nil
                         :logout-testid nil
                         :explanation-path "/anmeldung/login/#anmeldung-im-browser"
                         :explanation (tr "How can I sign in with my ARS account?")}}]))

(defn render-signup [_result _match]
  [signup-view])


(defscene signup
  [signup-view])

(defn email-confirmation-required []
  [:div {:class "flex min-h-screen w-screen items-center justify-center bg-[#f3f3f6]"}
   [:div {:class "w-full max-w-[540px] rounded-lg bg-white p-8 shadow-lg"}
    [:p.my-2.text-base (tr "It looks like you didn't confirm your email yet.")]
    [:p.my-2.text-base (tr "Please check your inbox and click on the link provided.")]
    [:form.mt-4.pb-2 {:action "/auth/send-confirmation-link" :method :post}
     [ui/button {:variant :primary
                 :size 3
                 :class "w-full"
                 :type "submit"
                 :data-testid "auth-verify-resend-confirmation"}
      (tr "Re-send confirmation link")]]
    [:a.text-sm.text-indigo-700.hover:text-indigo-800.hover:underline {:href "/logout"}
     (tr "Logout")]]])

(defn render-verify-email [_result _match] [email-confirmation-required])

(defscene show-email-confirmation-required
  [email-confirmation-required])

(defn forgot-password-view []
  (r/with-let [!success-message (r/atom nil)]
    (with-form [!form {:user/email ?email}
                :meta {'?email {:validators [(fn [email _]
                                               (when (and email (not (re-find util/email-regex email)))
                                                 {:type :invalid
                                                  :content (tr "Email must be valid")}))]}}
                :required [?email]]
      [auth-layout
       {:back-link {:href (router/path '[login])
                    :label (tr "Back to login")
                    :data-testid "auth-forgot-back-link"}
        :left-content
        [:form {:class "w-full space-y-5"
                :on-submit (fn [e]
                             (.preventDefault e)
                             (if (forms/submittable? !form)
                               (forms/watch-promise
                                !form
                                (p/let [result (http/$post "/auth/send-password-reset" @!form)]
                                  (handle-result result {:on-success #(reset! !success-message (tr "If the email exists, we sent a password reset link."))})))
                               (forms/touch! !form)))}
         [:h1 {:class "text-center font-bold leading-tight text-[24px] md:text-left"}
          (tr "Forgot your password")]
         [:div.flex.justify-center.md:justify-start
          [ui/explain-link {:path "/anmeldung/passwort-vergessen/"}
           (tr "How can I reset my password?")]]
         [:p {:class "text-[14px] leading-5 text-slate-700"}
          (tr "Please enter your email address to reset your password. You can use that email to choose a new password.")]

         (when @!success-message
           [:div.py-1
            [ui/flash-notice {:type :success
                              :message @!success-message
                              :on-close #(reset! !success-message nil)}]])

         [ui/with-label
          [:span.text-sm.font-semibold (tr "Email address")]
          [forms.ui/input-text ?email
           {:data-testid "auth-forgot-email"
            :size 3
            :placeholder "email@example.org"
            :class "w-full"}]]

         (into [:div.py-1] (map forms.ui/view-message) (forms/visible-messages !form))

         [ui/button {:variant :primary
                     :size 3
                     :class "w-full"
                     :data-testid "auth-forgot-submit"
                     :type "submit"}
          (tr "Reset password")]]

        :right-panel-opts {:oauth-testid nil
                           :logout-testid nil
                           :explanation-path "/anmeldung/login/#anmeldung-im-browser"
                           :explanation (tr "How can I sign in with my ARS account?")}}])))

(defscene forgot-password
  [forgot-password-view])

(defn render-forgot-password [_result _match] [forgot-password-view])

(defn reset-password-view [state]
  (with-form [!form {:user/password ?password
                     :user/password-confirmation ?password-confirmation
                     :user/password-reset-token ?password-reset-token}
              :meta {'?password-reset-token {:init (:user/password-reset-token state)}
                     '?password-confirmation {:validators [(fn [confirm {:syms [?password]}]
                                                             (when (not= confirm @?password)
                                                               {:type :invalid
                                                                :content (tr "Password and confirmation must match")}))]}}
              :required [?password ?password-confirmation]]
    [auth-layout
     {:back-link {:href (router/path '[login])
                  :label (tr "Back to login")
                  :data-testid "auth-reset-back-link"}
      :left-content
      [:form {:class "w-full space-y-5"
              :on-submit (fn [e]
                           (.preventDefault e)
                           (if (forms/submittable? !form)
                             (forms/watch-promise
                              !form
                              (p/let [result (http/$post "/auth/reset-password" @!form)]
                                (handle-result result)))
                             (forms/touch! !form)))}
       [:h1 {:class "text-center font-bold leading-tight text-[24px] md:text-left"}
        (tr "Set a new password")]

       [ui/with-label
        [:span.text-sm.font-semibold (tr "Password")]
        [forms.ui/input-text ?password
         {:data-testid "auth-reset-password"
          :size 3
          :type "password"
          :class "w-full"}]]
       [ui/with-label
        [:span.text-sm.font-semibold (tr "Repeat password")]
        [forms.ui/input-text ?password-confirmation
         {:data-testid "auth-reset-password-confirmation"
          :size 3
          :type "password"
          :class "w-full"}]]

       (into [:div.py-1] (map forms.ui/view-message) (forms/visible-messages !form))

       [ui/button {:variant :primary
                   :size 3
                   :class "w-full"
                   :data-testid "auth-reset-submit"
                   :type "submit"}
        (tr "Reset password")]
       [:div.flex.justify-center.md:justify-start
        [ui/explain-link {:path "/anmeldung/passwort-vergessen/"}
         (tr "How can I reset my password?")]]]

      :right-panel-opts {:oauth-testid nil
                         :logout-testid nil
                         :explanation-path "/anmeldung/login/#anmeldung-im-browser"
                         :explanation (tr "How can I sign in with my ARS account?")}}]))

(defscene reset-password
  [reset-password-view {:user/password-reset-token (random-uuid)}])

(defn render-reset-password [_result match]
  [reset-password-view {:user/password-reset-token (get-in match [:path-params :password-reset-token])}])
